import type { Link as TLink } from '@frontend/libs/types/common'
import type React from 'react'

import { isMobileDevice } from '@frontend/libs/utils'
import { Button } from '@nextui-org/react'
import { Link } from 'react-router-dom'

import VideoPlayer, { type VideoPlayerProps } from '../video-player/VideoPlayer'

interface Image {
  src: string
}

interface Images {
  desktop: Image
  mobile: Image
}

interface AspectRatios {
  desktop: string
  mobile: string
}

export type BannerProps = TBannerImage | TBannerVideo

export type TBanner = {
  aspectRatios?: AspectRatios
  buttonText?: string
  description?: string
  link: TLink
  subTitle?: string
  title?: string
}

export type TBannerImage = {
  images: Images
  type: 'image'
} & TBanner

export type TBannerVideo = {
  type: 'video'
  VideoPlayerProps: VideoPlayerProps
} & TBanner

const BannerContent: React.FC<
  Pick<
    BannerProps,
    'buttonText' | 'description' | 'link' | 'subTitle' | 'title'
  >
> = ({ buttonText, description, link, subTitle, title }) => (
  <div className="absolute bottom-[4.5rem] left-0 right-0 z-20 mx-auto max-w-screen-lg px-10">
    <div className="text-center">
      {subTitle && <span className="text-white">{subTitle}</span>}
      {title && (
        <h4 className="mb-4 text-xl font-medium uppercase text-white md:text-2xl">
          {title}
        </h4>
      )}
      {description && <div className="text-base text-white">{description}</div>}
      {link && (
        <Button
          as={Link}
          className="!hover:opacity-100 h-auto bg-white px-4 py-3 font-medium uppercase data-[hover=true]:bg-white"
          target={link.target}
          to={link.url}
          variant="light"
        >
          {buttonText}
        </Button>
      )}
    </div>
  </div>
)

export function Banner(props: Readonly<BannerProps>) {
  if (props.type === 'video') {
    const { VideoPlayerProps } = props
    let ratio = isMobileDevice
      ? props.aspectRatios?.mobile
      : props.aspectRatios?.desktop
    ratio = ratio?.replace('/', ':')
    return (
      <div
        className={`relative h-full w-full`}
        style={{
          aspectRatio: isMobileDevice
            ? props.aspectRatios?.mobile
            : props.aspectRatios?.desktop,
        }}
      >
        <VideoPlayer {...VideoPlayerProps} ratio={ratio} />
        <BannerContent {...props} />
      </div>
    )
  }

  const { desktop, mobile } = props.images || {
    desktop: { src: '' },
    mobile: { src: '' },
  }

  return (
    <div
      className={`box-overlay relative h-full w-full bg-cover bg-center bg-no-repeat`}
      style={{
        aspectRatio: isMobileDevice
          ? props.aspectRatios?.mobile
          : props.aspectRatios?.desktop,
        backgroundImage: `url(${isMobileDevice ? mobile?.src : desktop?.src})`,
      }}
    >
      <BannerContent {...props} />
    </div>
  )
}

export default Banner
